/*
 * OnDeviceTransaction.js
 * The payment / refund that is running in the payment app of THIS device (Verifone today, PAX and other
 * on-device solutions later). Nothing here is specific to one vendor.
 *
 * An on-device terminal can sell by itself (the POS page on the terminal: origin 'local') and can also serve
 * a request from another POS that arrives over SocketHub (origin 'remote', it has a txId). Both start with
 * OnDeviceTx.begin(), which writes ONE record:
 *
 *   { provider: 'verifone',          the `action` of the message (which payment app)
 *     kind:     'payment'|'refund',
 *     origin:   'local'|'remote',
 *     txId:     string|null,         remote only: the transaction of the asking POS
 *     payload:  object,              the message that was sent to the app (retry sends it again)
 *     startedAt: ms }
 *
 * The app's result is handled in ReactNativeMessageHandler.js ('verifone_state'):
 *   OnDeviceTx.remote()  -> report the outcome to the asking POS (reportToPos)
 *   OnDeviceTx.local()   -> update this page's own UI
 * and OnDeviceTx.clear() when the result is handled.
 *
 *   OnDevice.start(data.payment_data);               // local sale: records it (OnDeviceTx.begin) and opens the payment app
 *   OnDevice.start(req.payload, { txId: req.txId }); // request of another POS (SocketUi.js)
 *
 *   OnDeviceTx.retry(terminalId).set({ paymentData })  what a POS keeps to retry a payment on a REMOTE terminal
 *   OnDeviceTx.retry(terminalId).get() / .clear()
 *
 * Replaces the localStorage keys verifoneProcessData, verifone_payment_data, verifone_refund_data and vt_<id>.
 * The old keys are still read, and always cleared, so a payment running while the page updates is not lost.
 */
(function (window) {
  'use strict';

  var PROCESS_KEY = 'onDeviceTransaction';
  var RETRY_PREFIX = 'onDeviceRetry_';
  var LEGACY_PROCESS = 'verifoneProcessData';
  var LEGACY_PAYMENT = 'verifone_payment_data';
  var LEGACY_REFUND = 'verifone_refund_data';
  var LEGACY_RETRY_PREFIX = 'vt_';

  function read(key) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  function write(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  }
  function drop(key) {
    try {
      localStorage.removeItem(key);
    } catch (e) {}
  }
  function kindOf(message) {
    var a = message && message.data && message.data.action;
    return a === 'start_refund' ? 'refund' : 'payment';
  }

  function get() {
    var rec = read(PROCESS_KEY);
    if (rec) return rec;

    // written by the previous version of the page (a request of another POS)
    var old = read(LEGACY_PROCESS);
    if (old) {
      return {
        provider: old.type || null,
        kind: old.action === 'start_refund' ? 'refund' : 'payment',
        origin: 'remote',
        txId: old.txId || null,
        payload: old.paymentData || null,
        startedAt: 0,
      };
    }
    // ... or a sale of this device's own POS page
    var local = read(LEGACY_PAYMENT);
    if (local) {
      return {
        provider: local.action || null,
        kind: kindOf(local),
        origin: 'local',
        txId: null,
        payload: local,
        startedAt: 0,
      };
    }
    return null;
  }

  window.OnDeviceTx = {
    // remember a payment / refund before it is sent to the payment app
    begin: function (message, opts) {
      opts = opts || {};
      var rec = {
        provider: (message && message.action) || null,
        kind: kindOf(message),
        origin: opts.txId ? 'remote' : 'local',
        txId: opts.txId || null,
        payload: message,
        startedAt: Date.now(),
      };
      write(PROCESS_KEY, rec);
      return rec;
    },
    get: get,
    // the transaction when another POS asked for it (it has a txId to report to), else null
    remote: function () {
      var rec = get();
      return rec && rec.txId ? rec : null;
    },
    // the transaction when this device's own POS page started it, else null
    local: function () {
      var rec = get();
      return rec && !rec.txId ? rec : null;
    },
    clear: function () {
      drop(PROCESS_KEY);
      drop(LEGACY_PROCESS);
      drop(LEGACY_PAYMENT);
      drop(LEGACY_REFUND);
    },
    // POS side: keeps what the Retry button of a REMOTE terminal needs ({ paymentData, locationCart })
    retry: function (terminalId) {
      var key = RETRY_PREFIX + terminalId;
      var legacy = LEGACY_RETRY_PREFIX + terminalId;
      return {
        get: function () {
          return read(key) || read(legacy);
        },
        set: function (obj) {
          write(key, obj);
        },
        clear: function () {
          drop(key);
          drop(legacy);
        },
      };
    },
  };
})(window);
